import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl';
import { Loading } from "@gpstrackit/react-controls";
import { HasPermission } from 'Core/security';
import { ErrorFallback } from 'Components/ErrorBoundary';

import {
    authGetPreferencesRequest,
    authGetAuthenticationDetailsRequest,
    searchCountNotificationsRequest
} from 'Redux/actions';

const getFirstAvailablePath = (user) => {
    const initialPaths = [
        { path: "/app/dashboard/main", allowed: HasPermission(user, "pmdash") },
        { path: "/app/dealers/list", allowed: HasPermission(user, "dea") },
        { path: "/app/accounts/list", allowed: HasPermission(user, "cmp") },
        { path: "/app/importAccount/list", allowed: HasPermission(user, "mgrtns") },
        { path: "/app/useractivitylogs/list", allowed: HasPermission(user, "act") },
        { path: "/app/keyfobs/list", allowed: HasPermission(user, "kfb") },
        { path: "/app/scheduledactions/list", allowed: HasPermission(user, "schact") },
        { path: "/app/unsubscribedcontacts/list", allowed: HasPermission(user, "unsbctc") },
        { path: "/app/maintenancewarningevents/list", allowed: HasPermission(user, "wrgs") },
        { path: "/app/tags/list", allowed: HasPermission(user, "pmtag") },
        { path: "/app/devices/list", allowed: HasPermission(user, "unt") },
        { path: "/app/reports/list", allowed: HasPermission(user, "rpt") },
        { path: "/app/transactions/list", allowed: HasPermission(user, "trnsrqst") },
        { path: "/app/accounts/videoreview", allowed: HasPermission(user, "vdrvw") },
        { path: "/app/vidfleet/firmwaresreview", allowed: HasPermission(user, "vdfltfw") },
        { path: "/app/vidfleet/maintenancewarnings", allowed: HasPermission(user, "manw") },
        { path: "/app/vidfleet/cameralogs", allowed: HasPermission(user, "camlog") },
        { path: "/app/vidfleet/dmsstatuses", allowed: HasPermission(user, "dmst") },
        { path: "/app/vidfleet/powerlost", allowed: HasPermission(user, "pwlst") },
        { path: "/app/vidfleet/sdcardstatus", allowed: HasPermission(user, "sdcards") },
        { path: "/app/vidfleet/whitelisteddevices", allowed: HasPermission(user, "wlstd") },
        { path: "/app/manufacturers/list", allowed: HasPermission(user, "man") },
        { path: "/app/alerttypes/list", allowed: HasPermission(user, "alrttp") },
        { path: "/app/events/list", allowed: HasPermission(user, "evnt") },
        { path: "/app/simproviders/list", allowed: HasPermission(user, "simp") },
        { path: "/app/simprovideraccounts/list", allowed: HasPermission(user, "simpa") },
        { path: "/app/smssystems/list", allowed: HasPermission(user, "smss") },
        { path: "/app/brands/list", allowed: HasPermission(user, "brnd") },
        { path: "/app/pmusers/list", allowed: HasPermission(user, "usr") },
        { path: "/app/packages/list", allowed: HasPermission(user, "rol") },
        { path: "/app/pmusers/jirasummary", allowed: HasPermission(user, "js") },
        { path: "/app/cloudloginpage", allowed: HasPermission(user, "lgnpage") }
    ];

    return initialPaths.find((item) => item.allowed)?.path || null;
}

const AuthContainer = (props) => {
    const { children, history } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const location = useLocation()
    //Store
    const loading = useSelector(state => state.securityRedux.loading);
    const user = useSelector(state => state.securityRedux.user);
    const authDetailsError = useSelector(state => state.securityRedux.authDetailsError);
    const { messages } = useIntl();

    const authCheck = () => {
        let token = localStorage.getItem("IdToken");
        if (token) {
            dispatch(authGetPreferencesRequest());
            dispatch(searchCountNotificationsRequest());
        }
        if (!user && token) {
            dispatch(authGetAuthenticationDetailsRequest())
        }
        if (!token) {
            navigate('/login');
        }
    }

    useEffect(() => {
        authCheck();
    }, [])

    useEffect(() => {
        const currentUrl = location.pathname
        if (user && ['/app', '/error'].includes(currentUrl)) {
            const initialPath = getFirstAvailablePath(user);
            if (initialPath && initialPath !== currentUrl) {
                navigate(initialPath, { replace: true })
            }
        }
    }, [location.pathname, navigate, user])

    // The session details request failed (API unreachable, wrong endpoint...).
    // Without this the shell rendered nothing and the page stayed blank.
    if (!loading && !user && authDetailsError) {
        return (
            <ErrorFallback
                title={messages['auth_details_error_title']}
                description={messages['auth_details_error_message']}
                retryLabel={messages['auth_details_error_retry']}
                reloadLabel={messages['auth_details_error_reload']}
                onRetry={() => dispatch(authGetAuthenticationDetailsRequest())}
                onReload={() => window.location.reload()}
            />
        )
    }

    return (
        <>
            {
                loading &&
                <Loading overlay show />
            }
            {
                user &&
                <>
                    {children}
                </>
            }
        </>
    )
}
export default AuthContainer;
